import type { RegisteredEditorModule } from '../../types';
import {
  createRebusGame,
  duplicateRebus,
  getRebusSource,
  replaceRebus,
  updateJogo,
  TIPO_JOGO,
} from '../../../../../services/admin';
import { createEmpty, type RebusDoc } from './doc';
import { coalesceKeyFor, rebusReducer, type RebusAction } from './reducer';
import { validate } from './validate';
import { docToPayload, rowToDoc } from './wire';
import { initialRebusUi } from './ui';
import { Canvas } from './Canvas';
import { Inspector } from './Inspector';

// Monta o EditorModule do rébus: núcleo puro + serviços (via wire) + UI. O shell só
// conhece esta abstração — e este é o módulo que prova que ele não presume grade:
// não há `dimensions`, não há `deriveIndices`, não há coordenada nenhuma.

// Rébus não tem numeração derivada: `indices` é sempre vazio (não há `deriveIndices`).
const NO_INDICES: Record<string, number> = {};

export const rebusModule: RegisteredEditorModule<RebusDoc, RebusAction> = {
  kind: 'rebus',
  label: 'Rébus',

  // Rébus é JOGO (não passatempo), e o rótulo do botão do catálogo NÃO é o default:
  // "Editar grade" num jogo sem grade nenhuma seria mentira.
  entry: { catalog: 'jogo', idTipo: TIPO_JOGO.REBUS, editLabel: 'Editar rébus' },

  newPuzzle: {
    title: 'Novo rébus',
    description: 'Um rébus novo — uma imagem para decifrar, uma dica e a resposta.',
    titlePlaceholder: 'Ex.: Charada do gato',
    notice: {
      message: 'A imagem entra depois do primeiro save',
      description:
        'A imagem é enviada para um jogo que já existe. Preencha título, dica e solução, ' +
        'salve — e aí o botão de subir imagem libera. Sem imagem o rébus não é jogável, ' +
        'então o save seguinte passa a cobrá-la.',
    },
    // SEM `dimensions`: o rébus não tem grade, então o modal não pergunta largura nem
    // altura (o `createEmpty` recebe 0 × 0 e ignora os dois). É o caso que motivou o
    // campo ser opcional no contrato.
    //
    // Sem `tipo` também: o `id_tipo` do jogo é fixado por `createRebusGame`.
  },

  createEmpty,
  // Sem estado de seleção/arrasto (rébus não tem grade): só existe para coordenar os
  // dois pontos de upload (Canvas + Inspector). Ver `ui.ts`.
  initialUi: initialRebusUi,
  reduce: rebusReducer,
  validate,
  coalesceKey: coalesceKeyFor,

  load: (id) =>
    getRebusSource(id).then((row) => ({
      // O show_rebus não informa título nem ranking. Título vazio aqui (como no
      // caça-palavras): o autor completa no Inspector, e o save só o reenvia se ele
      // estiver preenchido — senão salvar logo após abrir apagaria o título real.
      // Ranking não informado → `editavel: true`; quem barra edição indevida de
      // verdade é o 409 do servidor no save.
      doc: rowToDoc(row, id, ''),
      editavel: true,
    })),

  save: (id, doc) => {
    const payload = docToPayload(doc);

    if (id != null) {
      // Aqui mora a divergência de verbo que o shell não pode conhecer: o rébus faz
      // replace por PATCH, o caça-palavras por PUT (§6.1 do design). Daqui para fora
      // os dois são só `save`.
      return replaceRebus(id, payload).then(() => {
        if (doc.titulo.trim() === '') return { id, indices: NO_INDICES };
        return updateJogo(id, { titulo: doc.titulo.trim() }).then(
          () => ({ id, indices: NO_INDICES }),
          (err: unknown) => {
            // O rébus já foi gravado; só o título falhou. O erro sobe (o autor pediu
            // para salvar um título e isso não aconteceu), mas a mensagem registra
            // que o resto está seguro.
            const detail = err instanceof Error ? err.message : String(err);
            throw new Error(
              `O rébus foi salvo, mas o título não pôde ser atualizado: ${detail}. ` +
                'Tente salvar novamente para reenviar o título.',
            );
          },
        );
      });
    }

    // Criação: dois passos (create_jogo e create_rebus). Quando o segundo falha, a
    // mensagem que vem de lá já diz qual jogo ficou órfão e que ele precisa ser
    // excluído (ver services/admin.ts); aqui ela só ganha o contexto da ação.
    return createRebusGame({ titulo: doc.titulo.trim(), ...payload }).then(
      (r) => ({ id: r.id, indices: NO_INDICES }),
      (err: unknown) => {
        // TS 4.5.5 não tem o `Error(message, { cause })` do ES2022 — o detalhe
        // original vai concatenado mesmo.
        const detail = err instanceof Error ? err.message : String(err);
        throw new Error(
          'Falha ao criar o rébus. Se o jogo já tiver sido criado no servidor (a criação ' +
            'acontece em duas etapas), tentar salvar de novo agora criaria outro registro ' +
            `órfão. Confira com o time antes de tentar de novo. Detalhe: ${detail}`,
        );
      },
    );
  },

  // Não existe prévia de rébus no servidor, e não faz falta: não há blob para
  // compilar nem numeração para conferir — o Canvas já mostra exatamente o que o
  // jogador vê. O contrato do shell exige `preview`, então ele RECUSA com a
  // explicação em vez de fingir sucesso: o shell só sabe dizer "Prévia gerada no
  // servidor" quando a promessa resolve, e isso seria mentira.
  preview: () =>
    Promise.reject(
      new Error(
        'O rébus não tem prévia no servidor — o editor já mostra a imagem, a dica e a ' +
          'solução exatamente como o jogador vai ver.',
      ),
    ),

  // ⚠️ NÃO é o `duplicateJogo` genérico: aquele bate em /admin/jogos/:id/duplicate,
  // que é do controller do caça-palavras e devolve 404 para um rébus.
  duplicate: duplicateRebus,

  Canvas,
  Inspector,
};
